/** @file MenuDropdown: the rows of one open menu-bar menu, rebuilt whenever the store changes. */
import { chordLabel } from '@zilo/pose3d-shortcuts/chordLabel';
import type { ReactElement } from 'react';
import { useEditor } from '../state/useEditor';
import type { MenuBuilder, MenuContext } from './menuTypes';
import { PLATFORM } from './platform';

/** `ctx` is what the builder reads; `onPick` runs after a row was chosen (it closes the menu). */
export type MenuDropdownProps = { label: string; build: MenuBuilder; ctx: MenuContext; onPick: () => void };

/** A `role="menu"` under its button; a row is a button that closes the menu, then acts. */
export function MenuDropdown({ label, build, ctx, onPick }: MenuDropdownProps): ReactElement {
  useEditor((s) => s);
  return (
    <div className="p3-menu p3-menubar-drop" role="menu" aria-label={label}>
      {build(ctx).map((item, i) => {
        if (item.kind === 'separator') return <div key={`sep-${String(i)}`} className="p3-menu-sep" role="separator" />;
        const toggle = item.checked !== undefined;
        return (
          <button key={item.id} type="button" role={toggle ? 'menuitemcheckbox' : 'menuitem'} aria-checked={toggle ? item.checked : undefined}
            aria-label={item.label} className="p3-menu-item" disabled={item.disabled} title={item.hint}
            onClick={() => { onPick(); item.onSelect(); }}>
            <span className="p3-menu-check" aria-hidden="true">{item.checked === true ? '✓' : ''}</span>
            <span className="p3-menu-label">{item.label}</span>
            {item.chord === undefined ? null : <span className="p3-muted p3-mono">{chordLabel(item.chord, PLATFORM)}</span>}
          </button>
        );
      })}
    </div>
  );
}
